import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.css";
import { getOrderItems, getOrderSummary } from "../../../utils/functions";
import usePageView from "../../../analytics/usePageView";

// "Items in the package" modal from the Claude Design file "OTP & Package Modals.dc.html".
// Centered dialog by default; a bottom sheet below 560px.

const inr = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || value === "NA" || Number.isNaN(n)) return value ?? "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const Items = ({ data, close: closeProp }) => {
  const closeRef = useRef(null);
  // Callers pass an inline function; keep the latest so the effect below runs once.
  const closeFn = useRef(closeProp);
  closeFn.current = closeProp;
  const close = useCallback(() => closeFn.current && closeFn.current(), []);
  const items = getOrderItems(data?.orderDetail).map((item) => {
    const known = item.name && item.name !== "NA";
    return { ...item, known, label: known ? item.name : "Item name not available", initial: known ? String(item.name).trim().charAt(0).toUpperCase() : "#" };
  });
  const { total, quantity } = getOrderSummary(data?.orderDetail);
  const count = quantity || items.length;
  usePageView("items");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus({ preventScroll: true, focusVisible: false });
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [close]);

  return createPortal(
    <div className={styles.wrap}>
      <div className={styles.overlay} onClick={close} />
      <div role="dialog" aria-modal="true" aria-label="Items in the package" className={styles.sheet}>
        <span className={styles.grab} />
        <div className={styles.head}>
          <div className={styles.headLeft}>
            <span className={styles.badge}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
                <path d="M3 8l9 5 9-5M12 13v8" />
              </svg>
            </span>
            <div className={styles.headText}>
              <h2 className={styles.title}>Items in the package</h2>
              <span className={styles.summary}>
                {count} item{count === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <button ref={closeRef} type="button" aria-label="Close" className={styles.close} onClick={close}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className={styles.list}>
          <div className={styles.colHead}>
            <span>Product name</span>
            <span className={styles.center}>Qty</span>
            <span className={styles.right}>Price</span>
          </div>
          {items.map((it) => (
            <div key={it.key} className={styles.row}>
              <div className={styles.product}>
                <span className={styles.initial}>{it.initial}</span>
                <div className={styles.nameBlock}>
                  <span className={`${styles.name} ${it.known ? "" : styles.nameMuted}`}>{it.label}</span>
                  <span className={styles.qtyInline}>Qty {it.quantity ?? "—"}</span>
                </div>
              </div>
              <span className={styles.qty}>{it.quantity ?? "—"}</span>
              <span className={styles.price}>{inr(it.value)}</span>
            </div>
          ))}
        </div>

        <div className={styles.foot}>
          <span className={styles.footLabel}>Total value</span>
          <span className={styles.total}>{inr(total)}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Items;
